<script setup>
import axios from 'axios';
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Loading from '../../Loading.vue';
import XlButton from '../../xl/XlButton.vue';
import XlEmptyState from '../../xl/XlEmptyState.vue';
import XlTabs from '../../xl/XlTabs.vue';
import { TERM_TIPS } from '../../../helpers/termTips';
import PageHeader from '../../PageHeader.vue';
import SegmentsTab from './SegmentsTab.vue';
import TemplatesTab from './TemplatesTab.vue';
import SequencesTab from './SequencesTab.vue';
import HistoryTab from './HistoryTab.vue';
import SendMessageTab from './SendMessageTab.vue';
import { useCommunicationsStore } from '../../../stores/communications';
import { useAuthStore } from '../../../stores/auth';
import { canAssignBrokers, hubReader } from '../../../helpers/roles';
import { hubEmptyState } from '../../../helpers/hubEmptyStates';
import { listHistory } from '../../../api/history';

// Three of these are terms staff meet here for the first time, so the tab that
// names one also explains it.
const TABS = [
  { id: 'segments', label: 'Segments', tooltip: TERM_TIPS.Segments },
  { id: 'templates', label: 'Templates', tooltip: TERM_TIPS.Templates },
  { id: 'sequences', label: 'Sequences', tooltip: TERM_TIPS.Sequences },
  { id: 'send', label: 'Send a message' },
  { id: 'history', label: 'History' },
];
const TAB_COMPONENTS = {
  segments: SegmentsTab,
  templates: TemplatesTab,
  sequences: SequencesTab,
  send: SendMessageTab,
  history: HistoryTab,
};
const DEFAULT_TAB = TABS[0].id;

const route = useRoute();
const router = useRouter();
const store = useCommunicationsStore();
const auth = useAuthStore();

// A broker works inside what the agency defined, and the page says so rather
// than describing a scope they do not have.
const blurb = computed(() => (canAssignBrokers(auth.userData)
  ? 'Who gets what, what repeats automatically, and everything the agency has sent.'
  : 'Everything here is scoped to your own policyholders. The agency sets the segments and sequences — you send within them.'));

// Selected via ?tab=, so a link can deep-link straight to e.g. History
// instead of always landing on the first tab. A ?tab= naming no tab falls back
// to the first, rather than rendering a panel the tab strip does not offer.
const activeTab = computed(() => {
  const wanted = route.query.tab;
  return TABS.some((tab) => tab.id === wanted) ? wanted : DEFAULT_TAB;
});

const selectTab = (tabId) => {
  if (tabId === activeTab.value) return;
  router.replace({ query: { ...route.query, tab: tabId } });
};

watch(activeTab, () => {
  store.clearToolbar();
  store.setEditing(false);
});

const api = () => window.store?.event_backend_api ?? '/api';

const counts = ref(null);
const needsRenewalDates = ref(false);
let renewalGapRequested = false;

const everSent = ref(null);

const checkHistory = async () => {
  try {
    const { meta } = await listHistory({ perPage: 1 });
    everSent.value = (meta?.total ?? 0) > 0;
  } catch {
    everSent.value = true;
  }
};

const loadCounts = async () => {
  try {
    const { data } = await axios.get(`${api()}/access/counts`);
    counts.value = data?.data ?? {};
  } catch {
    counts.value = {};
  }
  if (nobodyYet.value) await checkHistory();
};

const registeredTotal = async (params) => {
  const { data } = await axios.get(`${api()}/users`, { params: { per_page: 1, ...params } });

  return data?.registered?.total ?? 0;
};

const loadRenewalGap = async () => {
  renewalGapRequested = true;
  try {
    const [all, undated] = await Promise.all([registeredTotal({}), registeredTotal({ renewal_date: 'not_set' })]);
    needsRenewalDates.value = all === undated;
  } catch {
    needsRenewalDates.value = false;
  }
};

watch(activeTab, (tab) => {
  if (tab === 'sequences' && !renewalGapRequested) loadRenewalGap();
}, { immediate: true });

const nobodyYet = computed(() => (counts.value === null ? null : hubEmptyState('communications', {
  reader: hubReader(auth),
  role: auth.userRole,
  capabilities: auth.capabilities,
  counts: counts.value,
})));
const pageState = computed(() => (everSent.value === false ? nobodyYet.value : null));
// Only a hub that may be on its first run waits for the answer before drawing
// anything. Every other hub draws its tabs and the open tab at once, with the
// tab's own skeleton: the page used to stand in one block of rows for all of
// it, then put the tabs and the tab's banner above the list when the counts
// came — the list landed ~85px lower ("Communications jumps as it opens").
const pending = computed(() => Boolean(nobodyYet.value) && everSent.value === null);

const onPageAction = () => router.push({ path: '/policyholders', query: { tab: 'registered', create: 'invite' } });

onMounted(() => {
  store.fetchSettings();
  loadCounts();
});
</script>

<template>
  <PageHeader />

  <div class="page-content" data-xl-gen="northstar">
    <div class="comms">
      <header class="comms__head">
        <h1>Communications</h1>
        <p>{{ blurb }}</p>
      </header>

      <Loading v-if="pending" variant="rows" :rows="3" data-testid="comms-loading" />
      <XlEmptyState
        v-else-if="pageState"
        variant="page"
        :icon="pageState.icon"
        :headline="pageState.headline"
        :body="pageState.body"
        :actions="pageState.actions"
        data-testid="comms-empty"
        @action="onPageAction"
      />
      <template v-else>
      <XlTabs v-if="!store.editing" :tabs="TABS" :model-value="activeTab" @update:model-value="selectTab" />

      <div class="comms__panel">
        <!--
          Handed to the tab so it lands in the same row as that tab's own
          button. Only while something is selected: a tab registers its delete
          on mount, so keying on the action alone left the control sitting
          above an untouched list with nothing to act on.
        -->
        <component :is="TAB_COMPONENTS[activeTab]" v-bind="activeTab === 'sequences' ? { needsRenewalDates } : {}">
          <template #actions>
            <XlButton
              v-if="store.toolbar.onDelete && !store.toolbar.deleteDisabled"
              data-testid="comms-delete"
              variant="ghost-danger"
              size="sm"
              @click="store.toolbar.onDelete?.()"
            >Delete</XlButton>
          </template>
        </component>
      </div>
      </template>
    </div>
  </div>
</template>

<style scoped>
.comms, .comms * { box-sizing: border-box; }

.comms {
  padding-bottom: var(--setup-page-padding, 40px);
  font-family: var(--xl-font-family);
  color: var(--xl-color-primary-text);
}

.comms__head { margin-bottom: 18px; }

.comms__head h1 {
  margin: 0 0 6px;
  font-size: 30px;
  font-weight: var(--xl-font-weight-bold);
  letter-spacing: -0.03em;
  color: var(--xl-color-primary-text);
}

.comms__head p {
  margin: 0;
  font-size: 15px;
  color: var(--xl-color-outline);
}

/* The prototype's field (.fld label, .inp, .selbtn, the counter under a
   field), held to this page so other screens' forms are not moved by it. */
.comms :deep(.xl-field__label) {
  font-size: 12.5px;
  line-height: normal;
}

.comms :deep(.xl-field__control:not(.xl-field__control--tick)) {
  border-radius: 11px;
  padding: 0 14px;
  font-size: 14.5px;
}

.comms :deep(.xl-field__control.xl-field__control--area) {
  padding: 11px 14px;
  line-height: 1.5;
}

.comms :deep(.xl-dropdown__control) {
  border-radius: 11px;
  font-size: 14.5px;
}

.comms :deep(.xl-dt__control) { border-radius: 11px !important; }

.comms :deep(.xl-dropdown__control) {
  min-width: 128px;
  text-align: left;
}

/* The board's .tbl: 14px rows under 46px faint headings, ruled in the lighter line. */
.comms :deep(.xl-table) { font-size: 14px; }

.comms :deep(.xl-table th) {
  height: 46px;
  color: var(--xl-color-outline-variant);
}

.comms :deep(.xl-table td) { border-bottom-color: var(--xl-color-border-subtle); }

.comms :deep(.xl-dropdown__chevron) {
  width: 15px;
  height: 15px;
  color: var(--xl-color-outline);
}

.comms :deep(.xl-dropdown.is-open .xl-dropdown__control) { box-shadow: 0 0 0 3px rgba(94, 47, 120, 0.1); }

.comms :deep(.xl-dropdown__chevron) { transition: transform 0.16s; }

.comms :deep(.xl-dropdown.is-open .xl-dropdown__chevron) {
  transform: rotate(180deg);
  color: var(--xl-color-primary);
}

.comms :deep(.xl-dropdown__menu) {
  top: calc(100% + 5px);
  border-radius: 14px;
  box-shadow: 0 18px 44px rgba(28, 23, 32, 0.18);
}

.comms :deep(.xl-dropdown__option) {
  gap: 8px;
  min-height: 36px;
  border-radius: 9px;
  font-size: 14px;
  line-height: 1.35;
  color: var(--xl-color-secondary-text);
}

.comms :deep(.xl-dropdown__option.is-chosen) { color: var(--xl-color-primary-text); }

.comms :deep(.xl-dropdown__option.is-active),
.comms :deep(.xl-dropdown__option:hover) {
  background: var(--xl-color-primary-container);
  color: var(--xl-color-primary);
}

/* Opening lands the keyboard on the chosen row; the board shows that row by its
   tick and weight alone until the pointer or an arrow key moves. */
.comms :deep(.xl-dropdown__option.is-chosen.is-active:not(:hover)) {
  background: transparent;
  color: var(--xl-color-primary-text);
}

.comms :deep(.xl-tooltip--underline) {
  text-decoration-color: var(--xl-color-outline-variant);
  text-decoration-thickness: 1px;
}

.comms :deep(.character-counter),
.comms :deep(.xl-field__counter) {
  display: block;
  font-size: 11.5px;
  font-weight: 400;
  color: var(--xl-color-outline-variant);
  text-align: right;
}

.comms :deep(.character-counter) { margin-top: 5px; }

.comms :deep(.xl-field__counter .character-counter) { margin-top: 0; }

/* XlField's own 6px gap less one: the board puts the counter 5px under its control. */
.comms :deep(.xl-field__foot) { margin-top: -1px; }

/* A board textarea sits inline, so its line box leaves 5px more under it. */
.comms :deep(.xl-field:has(> .xl-field__control--area) > .xl-field__foot) { margin-top: 4px; }

.comms :deep(.character-counter--near) { color: #b45309; }
.comms :deep(.character-counter--over) { color: #c0392b; }

/* One rhythm for the whole panel, so no two blocks sit flush together. */
/* The tab strip's own 22px bottom margin is the whole gap above the panel, as
   it is with the strip hidden and the header's 18px. */
.comms__panel {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
</style>
